// theme.js
// OurGenie brand theme — deep-lamp navy from the logo, warm brass accent
// pulled from the "genie" idea itself, parchment surfaces for calm reading.
//
// Usage (in your root, e.g. main.jsx / App.jsx):
//
//   import { ThemeProvider, CssBaseline } from "@mui/material";
//   import theme from "./theme";
//
//   <ThemeProvider theme={theme}>
//     <CssBaseline />
//     <App />
//   </ThemeProvider>
//
// Also add these two lines to the <head> of index.html so the type scale
// renders correctly everywhere (Sidebar's Tailwind classes rely on them too):
//
//   <link rel="preconnect" href="https://fonts.googleapis.com">
//   <link href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet">

import { createTheme } from "@mui/material/styles";

export const brand = {
  ink950: "#0B0A3D", // primary — straight from the logo card
  ink800: "#17155F",
  ink700: "#26235C",
  parchment: "#F7F4ED", // straight from the logo lettering
  paper: "#FFFFFF",
  brass: "#C9A227", // accent — the genie-lamp gold
  brassLight: "#E4C766",
  brassSoft: "#FBF3DC",
  slate: "#6E6E86",
  slateLight: "#C7C6D9",
  success: "#2F9E68",
  danger: "#D6455A",
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: brand.ink950, light: brand.ink700, contrastText: brand.parchment },
    secondary: { main: brand.brass, light: brand.brassLight, contrastText: brand.ink950 },
    error: { main: brand.danger },
    success: { main: brand.success },
    background: { default: brand.parchment, paper: brand.paper },
    text: { primary: brand.ink950, secondary: brand.slate },
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Fraunces", serif', fontWeight: 600 },
    h2: { fontFamily: '"Fraunces", serif', fontWeight: 600 },
    h3: { fontFamily: '"Fraunces", serif', fontWeight: 600 },
    h4: { fontFamily: '"Fraunces", serif', fontWeight: 600 },
    h5: { fontFamily: '"Fraunces", serif', fontWeight: 600, letterSpacing: "-0.01em" },
    h6: { fontFamily: '"Fraunces", serif', fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: brand.parchment },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: `1px solid ${brand.slateLight}55`,
        },
        elevation3: { boxShadow: "0 10px 30px -14px rgba(11,10,61,0.25)" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, paddingInline: 18 },
        containedPrimary: {
          backgroundColor: brand.ink950,
          "&:hover": { backgroundColor: brand.ink700 },
        },
        containedSecondary: {
          backgroundColor: brand.brass,
          color: brand.ink950,
          "&:hover": { backgroundColor: brand.brassLight },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: brand.ink950,
          "& .MuiTableCell-root": {
            color: brand.parchment,
            fontWeight: 600,
            fontFamily: '"Inter", sans-serif',
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            fontSize: "0.72rem",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: brand.brassSoft },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: '"Fraunces", serif', fontWeight: 600, fontSize: "1.35rem" },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small" },
    },
  },
});

export default theme;